import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useToast } from '../../../hooks/useToast';
import { FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS } from '../../../utils/facilityReservationDate';

interface FacilityForm {
  name: string;
  available: boolean;
  price: number;
  basePrice?: number; // 기본 이용가격 (시작종료형 전용, 선택)
  maxPrice?: number; // 최대 이용가격 (선택)
  notice: string;
  operatingHours: {
    start: number;
    end: number;
  };
  operatingDays: number[];
  imageUrl: string;
  reservationDays: number; // 예약 시작 가능일
  reservationWindowDays: number; // 예약 가능 기간
  reservationTimeUnit: number; // 예약시간단위
  reservationType: 'start-end' | 'time-slot'; // 예약타입
}

const FacilityNewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FacilityForm>({
    name: '',
    available: true,
    price: 0,
    basePrice: undefined,
    maxPrice: undefined,
    notice: '',
    operatingHours: {
      start: 9,
      end: 18
    },
    operatingDays: [1, 2, 3, 4, 5],
    imageUrl: '',
    reservationDays: 0,
    reservationWindowDays: FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
    reservationTimeUnit: 1,
    reservationType: 'start-end'
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 권한 체크 및 초기 설정
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilities)) {
        navigate('/admin/dashboard');
        return;
      }
    }
  }, [user, userLevel, permissions, authLoading, navigate]);

  // 폼 검증 함수
  const validateForm = useCallback((): boolean => {
    // 필수 필드 검증
    if (!formData.name.trim()) {
      showToast('시설물명을 입력해주세요.', { type: 'warning' });
      return false;
    }

    if (formData.price < 0) {
      showToast('이용가격은 0원 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.basePrice && formData.basePrice < 0) {
      showToast('기본 이용가격은 0원 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.maxPrice && formData.maxPrice < 0) {
      showToast('최대 이용가격은 0원 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.reservationDays < 0) {
      showToast('예약 시작 가능일은 0일 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.reservationWindowDays < 0) {
      showToast('예약 가능 기간은 0일 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.reservationWindowDays < formData.reservationDays) {
      showToast('예약 가능 기간은 예약 시작 가능일보다 크거나 같아야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.reservationTimeUnit < 1) {
      showToast('예약시간단위는 1시간 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    if (formData.operatingDays.length === 0) {
      showToast('최소 하나 이상의 운영요일을 선택해주세요.', { type: 'warning' });
      return false;
    }

    if (formData.operatingHours.start >= formData.operatingHours.end) {
      showToast('운영 종료시간이 시작시간보다 늦어야 합니다.', { type: 'warning' });
      return false;
    }

    return true;
  }, [formData, showToast]);

  // 이미지 업로드 처리
  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const imageRef = ref(storage, `facilities/${fileName}`);
      const metadata = {
        contentType: file.type,
        createdAt: new Date().toISOString()
      };

      await uploadBytes(imageRef, file, metadata);
      const url = await getDownloadURL(imageRef);
      return url;
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      throw new Error('이미지 업로드에 실패했습니다.');
    }
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 폼 검증
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    let docRef = null;
    let uploadedImageUrl = '';

    try {
      // 이미지가 선택된 경우 먼저 업로드
      if (selectedImage) {
        uploadedImageUrl = await handleImageUpload(selectedImage);
      }

      // 시설물 문서 생성
      const facilityData = {
        name: formData.name.trim(),
        price: Number(formData.price),
        basePrice: formData.basePrice ? Number(formData.basePrice) : null,
        maxPrice: formData.maxPrice ? Number(formData.maxPrice) : null,
        notice: formData.notice.trim(),
        available: true,
        operatingHours: formData.operatingHours,
        operatingDays: formData.operatingDays,
        imageUrl: uploadedImageUrl,
        reservationDays: Number(formData.reservationDays),
        reservationWindowDays: Number(formData.reservationWindowDays),
        reservationTimeUnit: Number(formData.reservationTimeUnit),
        reservationType: formData.reservationType,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      docRef = await addDoc(collection(db, 'facilities'), facilityData);

      showToast(`새로운 시설물 '${formData.name}'이 성공적으로 등록되었습니다.`, { type: 'success' });

      // 약간의 딜레이 후 페이지 이동
      setTimeout(() => {
        navigate('/admin/facilities');
      }, 1500);
    } catch (error) {
      console.error('시설물 등록 중 오류 발생:', error);

      let errorMessage = '시설물 등록 중 오류가 발생했습니다.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      showToast(errorMessage, { type: 'error' });

      // 문서가 생성되었지만 오류가 발생한 경우 정리
      if (docRef) {
        try {
          await deleteDoc(doc(db, 'facilities', docRef.id));
        } catch (deleteError) {
          console.error('문서 삭제 중 오류 발생:', deleteError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [formData, selectedImage, validateForm, handleImageUpload, navigate, showToast]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name === 'operatingHours.start' || name === 'operatingHours.end') {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [child]: Number(value)
        }
      }));
    } else if (name === 'operatingDays') {
      const day = Number(value);
      setFormData(prev => ({
        ...prev,
        operatingDays: prev.operatingDays.includes(day)
          ? prev.operatingDays.filter(d => d !== day)
          : [...prev.operatingDays, day].sort()
      }));
    } else {
      const key = name as keyof FacilityForm;
      setFormData(prev => ({
        ...prev,
        [key]: type === 'number' ? Number(value) : value
      }));
    }
  }, []);

  const handleImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 이미지 파일 유형 및 크기 검증
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        showToast('지원되는 이미지 형식: JPG, PNG, GIF, WebP', { type: 'warning' });
        return;
      }

      if (file.size > maxSize) {
        showToast('이미지 크기는 10MB 이하여야 합니다.', { type: 'warning' });
        return;
      }

      setSelectedImage(file);
    }
  }, [showToast]);

  // 권한 체크 - 인증이 완료된 후에만 확인
  const hasPermission = useMemo(() => {
    if (authLoading) return null;
    if (!user || !userLevel) return false;
    return userLevel === 1 || permissions?.facilities;
  }, [user, userLevel, permissions, authLoading]);

  // 상수 데이터 메모이제이션
  const weekdays = useMemo(() => [
    { value: 0, label: '일' },
    { value: 1, label: '월' },
    { value: 2, label: '화' },
    { value: 3, label: '수' },
    { value: 4, label: '목' },
    { value: 5, label: '금' },
    { value: 6, label: '토' }
  ], []);

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  // 통합된 로딩 상태 - 인증 중일 때
  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              권한을 확인하는 중...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 없는 경우
  if (hasPermission === false) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] mb-2">
                    접근 권한 없음
                  </h2>
                  <p className="text-yellow-700 text-lg font-medium font-['Pretendard'] mb-4">
                    새로운 시설물을 등록할 권한이 없습니다.
                  </p>
                  <button
                    onClick={() => navigate('/admin/facilities')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 transition-colors"
                  >
                    목록으로 돌아가기
                  </button>
                </div>
                <svg className="w-12 h-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 1112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 확인되지 않은 경우
  if (hasPermission !== true) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 등록
            </div>
            <button
              onClick={() => navigate('/admin/facilities')}
              className="w-[100px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
            >
              목록으로
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard'] mb-2">
                  새로운 시설물을 등록할 수 있습니다.
                </p>
                <p className="text-[#6b7684] text-sm">
                  기본 정보, 운영 정보, 예약 설정을 입력하여 시설물을 등록해주세요.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 시설물 등록 폼 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              시설물 등록 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 기본 정보 섹션 */}
              <div className="space-y-6">
                <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">기본 정보</h3>
                
                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">시설물명</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이용가격</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  />
                </div>

                {formData.reservationType === 'start-end' && (
                  <div>
                    <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">기본 이용가격 (선택)</label>
                    <input
                      type="number"
                      name="basePrice"
                      value={formData.basePrice || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                      placeholder="첫 1시간 기본 요금을 입력하세요"
                    />
                    <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                      기본 이용가격이 설정되면 첫 1시간은 기본 요금, 이후 시간은 시간당 이용가격이 적용됩니다.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">최대 이용가격 (선택)</label>
                  <input
                    type="number"
                    name="maxPrice"
                    value={formData.maxPrice || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    placeholder="최대 이용가격을 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">기타안내</label>
                  <textarea
                    name="notice"
                    value={formData.notice}
                    onChange={handleChange}
                    rows={4}
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                  />
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">대표 이미지</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-1 block w-full text-[#1e2c49] font-['Pretendard']"
                  />
                </div>
              </div>

              {/* 운영 정보 섹션 */}
              <div className="space-y-6">
                <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">운영 정보</h3>
                
                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">운영시간</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <select
                      name="operatingHours.start"
                      value={formData.operatingHours.start}
                      onChange={handleChange}
                      className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    >
                      {hours.map(hour => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                    <span className="text-[#1e2c49] font-['Pretendard']">~</span>
                    <select
                      name="operatingHours.end"
                      value={formData.operatingHours.end}
                      onChange={handleChange}
                      className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    >
                      {hours.map(hour => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">운영요일</label>
                  <div className="mt-2 space-x-4">
                    {weekdays.map(day => (
                      <label key={day.value} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          name="operatingDays"
                          value={day.value}
                          checked={formData.operatingDays.includes(day.value)}
                          onChange={handleChange}
                          className="rounded border-[#e5e8eb] text-[#155acb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb]"
                        />
                        <span className="ml-2 text-[#1e2c49] font-['Pretendard']">{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* 예약 설정 섹션 */}
              <div className="space-y-6">
                <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">예약 설정</h3>
                
                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 시작 가능일</label>
                  <input
                    type="number"
                    name="reservationDays"
                    value={formData.reservationDays}
                    onChange={handleChange}
                    min="0"
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  />
                  <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                    0이면 당일부터, 3이면 신청일 기준 3일 후부터 예약할 수 있습니다.
                  </p>
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 가능 기간</label>
                  <input
                    type="number"
                    name="reservationWindowDays"
                    value={formData.reservationWindowDays}
                    onChange={handleChange}
                    min="0"
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  />
                  <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                    30이면 신청일 기준 30일 후 날짜까지만 예약할 수 있습니다.
                  </p>
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약시간단위</label>
                  <input
                    type="number"
                    name="reservationTimeUnit"
                    value={formData.reservationTimeUnit}
                    onChange={handleChange}
                    min="1"
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약타입</label>
                  <select
                    name="reservationType"
                    value={formData.reservationType}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    required
                  >
                    <option value="start-end">시작종료형</option>
                    <option value="time-slot">시간대형</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center pt-6">
                <div className="text-sm text-[#6b7684]">
                  <span className="text-blue-600">* 모든 필수 정보를 정확히 입력해주세요.</span>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/facilities')}
                    disabled={loading}
                    className="px-4 py-2 border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        등록 중...
                      </div>
                    ) : (
                      '시설물 등록'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default FacilityNewPage; 
